import React, { useEffect, useMemo, useState, useRef, forwardRef, useImperativeHandle } from 'react'
import { View, StyleSheet, SectionList, Platform, TouchableOpacity } from 'react-native'
import { format, isEqual, parse } from 'date-fns'
import { Text, Card, RangeDatepicker, Spinner, Button } from '@ui-kitten/components'
import HeaderIconButton from './HeaderIconButton'
import { useTranslation, type TFunction } from 'react-i18next'
import { dateRangeWithTimezoneAndAutoclose, getLocalStoreDate } from '../utils/timezoneFunctions'
import { dbAtom, userStoreIdAtom, localeDateServiceAtom, userRoleAtom } from '../global/AtomsProvider'
import { localRowsOverlayAtom } from '../global/Write/localViewState'
import { idsOfRows } from '../global/Write/localViewOverlay'
import { localViewListMounted, observeLocalViewRows } from '../global/Write/localView'
import { mergePendingRows, pendingRowsForList, serverPlacementOf } from './operationsListPendingRows'
import { useAtomValue } from 'jotai'
import { shouldUseSupabase } from '../global/Firebase/types'
import { useColors } from '../utils/useColors'
import StoreChooserHorizontal from './StoreChooserHorizontal'
import { api } from '../global/API/apiClient'
import { exportToCSV, exportToExcel } from '../utils/exportUtils'
import { timestampLikeOf } from '../utils/timestampLike'
import { withSupplyOrderSupplierKey } from '../utils/supplyOrderLine'
import {
  resolveOperationsFilter,
  buildSupabaseListParams,
  type OperationsFilter,
  type FirestoreQuery,
} from './operationsListFilters'
import {
  salesApi,
  suppliesApi,
  writeoffsApi,
  spendingsApi,
  transfersApi,
  revisionsApi,
  transactionsApi,
  type SaleResponse,
  type SupplyResponse,
  type WriteoffResponse,
  type SpendingResponse,
  type TransferResponse,
  type RevisionResponse,
  type TransactionResponse,
} from '../global/API/typedApiClient'

// ============ TYPE DEFINITIONS ============

type CollectionName = 'sales' | 'supplies' | 'writeOffs' | 'spendings' | 'transfers' | 'revisions' | 'transactions' | 'orders' | 'clients' | 'manufactures' | 'supplyOrders' | 'updates'

type ApiResponse = SaleResponse | SupplyResponse | WriteoffResponse | SpendingResponse | TransferResponse | RevisionResponse | TransactionResponse

interface DateRange {
  startDate: Date | null | undefined
  endDate: Date | null | undefined
}

interface FirestoreTimestamp {
  toDate: () => Date
}

interface DataItem {
  id: string
  store?: string
  ref?: unknown
  firestore_id?: string
  isFromSecondCollection?: boolean
  [key: string]: unknown
}

interface DataSection {
  title: string
  data: DataItem[]
  index: number
}

interface SecondQuery {
  collectionName: string
  dateField?: string
  detailScreenName?: string
  /**
   * The second query is FIRESTORE-ONLY (`secondQuery` is never consulted on the
   * Supabase read path), so a bare closure is honest here rather than a paired
   * filter that would imply an API equivalent exists.
   */
  additionalFilters?: (query: FirestoreQuery) => FirestoreQuery
  merger?: (primaryData: DataItem[], item: DataItem) => DataItem[]
  sortMergedData?: boolean
}

// Export data transformer function type
type ExportDataTransformer = (items: DataItem[], t: TFunction) => Record<string, unknown>[]

interface OperationsListProps {
  navigation: {
    setOptions: (options: { headerRight?: () => React.ReactNode }) => void
    navigate: (screen: string, params?: Record<string, unknown>) => void
  }
  collectionName: CollectionName
  dateField: string
  /**
   * ATL-986: ONE filter carrying BOTH read paths. See `operationsListFilters.ts`.
   * A caller may not declare the Firestore half alone — the Supabase read path
   * refuses to render rather than silently returning unfiltered rows.
   */
  additionalFilters?: OperationsFilter | null
  showStoreFilter?: boolean
  preselectedStore?: string
  detailScreenName?: string
  detailScreenParams?: Record<string, unknown>
  renderItemFieldsToShow: (item: DataItem, date: string | null) => React.ReactNode
  showAddButton?: boolean
  topView?: () => React.ReactNode
  initialRange?: DateRange | 'allTime'
  showTotalSum?: boolean
  totalSumField?: string
  totalSumFormatter?: { format: (value: number) => string }
  additionalDataValidation?: (data: DataSection[], range: DateRange | undefined, store: string) => void
  secondQuery?: SecondQuery | null
  // Export functionality props
  showExportButtons?: boolean
  exportDataTransformer?: ExportDataTransformer
  exportFilenamePrefix?: string
}

export interface OperationsListRef {
  refresh: (passedRange?: DateRange, additionalFilters?: OperationsFilter | null) => void
}

// Export types for use in parent components
export type { DataItem, DataSection, ExportDataTransformer }

// ============ CONSTANTS ============

// Map collection names to typed API functions
const getTypedApiFetcher = (collectionName: CollectionName): ((params: Record<string, unknown>) => Promise<ApiResponse[]>) | null => {
  switch (collectionName) {
    case 'sales':
      return (params) => salesApi.getSales(params as Parameters<typeof salesApi.getSales>[0])
    case 'supplies':
      return (params) => suppliesApi.getSupplies(params as Parameters<typeof suppliesApi.getSupplies>[0])
    case 'writeOffs':
      return (params) => writeoffsApi.getWriteoffs(params as Parameters<typeof writeoffsApi.getWriteoffs>[0])
    case 'spendings':
      return (params) => spendingsApi.getSpendings(params as Parameters<typeof spendingsApi.getSpendings>[0])
    case 'transfers':
      return (params) => transfersApi.getTransfers(params as Parameters<typeof transfersApi.getTransfers>[0])
    case 'revisions':
      return (params) => revisionsApi.getRevisions(params as Parameters<typeof revisionsApi.getRevisions>[0])
    case 'transactions':
      return (params) => transactionsApi.getTransactions(params as Parameters<typeof transactionsApi.getTransactions>[0])
    default:
      return null // Fall back to untyped for unsupported collections (e.g., orders)
  }
}

// Map collection names to API endpoints (fallback for untyped)
const COLLECTION_TO_API_ENDPOINT: Record<CollectionName, string | null> = {
  'sales': 'sales',
  'supplies': 'supplies',
  'writeOffs': 'writeoffs',
  'spendings': 'spendings',
  'transfers': 'transfers',
  'revisions': 'revisions',
  'transactions': 'transactions',
  'orders': 'orders',
  'clients': 'clients',
  'manufactures': 'manufactures',
  'supplyOrders': 'supply-orders',
  'updates': 'updates',
}

// Map collection names to date field names in API responses (camelCase from backend)
const COLLECTION_TO_DATE_FIELD: Record<CollectionName, string> = {
  'sales': 'saleDate',
  'supplies': 'supplyDate',
  'writeOffs': 'date',
  'spendings': 'date',
  'transfers': 'date',
  'revisions': 'date',
  'transactions': 'date',
  'orders': 'date',
  'clients': 'registrationDate',
  'manufactures': 'date',
  'supplyOrders': 'date',
  'updates': 'date',
}

// Map Firestore dateField to API date field for each collection
const getApiDateField = (collectionName: CollectionName, firestoreDateField: string): string => {
  return COLLECTION_TO_DATE_FIELD[collectionName] || firestoreDateField
}

// ============ HELPER COMPONENTS ============

const Loader: React.FC = () => (
  // eslint-disable-next-line react-native/no-inline-styles
  <View style={{ alignItems: 'center', marginTop: 10 }}>
    <Spinner size="large" />
  </View>
)

interface WebRefreshControlProps {
  refreshFunction: () => void
  listRefreshing: boolean
  refreshText: string
  primaryColor: string
}

const WebRefreshControl: React.FC<WebRefreshControlProps> = ({ refreshFunction, listRefreshing, refreshText, primaryColor }) => (
  // eslint-disable-next-line react-native/no-inline-styles
  <View style={{ alignItems: 'center' }}>
    {listRefreshing ? (
      <Loader />
    ) : (
      <TouchableOpacity onPress={refreshFunction}>
        <Text style={{ color: primaryColor }}>{refreshText}</Text>
      </TouchableOpacity>
    )}
  </View>
)

// ============ MAIN COMPONENT ============

const OperationsList = forwardRef<OperationsListRef, OperationsListProps>(function OperationsList(
  {
    navigation,
    collectionName,
    dateField,
    additionalFilters,
    showStoreFilter,
    preselectedStore,
    detailScreenName,
    detailScreenParams,
    renderItemFieldsToShow,
    showAddButton,
    topView = () => null,
    initialRange,
    showTotalSum,
    totalSumField,
    totalSumFormatter,
    additionalDataValidation,
    secondQuery = null,
    showExportButtons = false,
    exportDataTransformer,
    exportFilenamePrefix,
  },
  ref
) {
  const { t } = useTranslation()
  const colors = useColors()
  const db = useAtomValue(dbAtom)
  const userStoreId = useAtomValue(userStoreIdAtom)
  const userRole = useAtomValue(userRoleAtom)
  // Check device-level setting for backend choice
  const useSupabase = shouldUseSupabase()
  const canExport = userRole?.permissions?.['exportImport'] || userRole?.id === 'admin'
  const [lastDocument, setLastDocument] = useState<unknown>()
  const [firstDocument, setFirstDocument] = useState<unknown>()
  const [secondLastDocument, setSecondLastDocument] = useState<unknown>()
  const [secondFirstDocument, setSecondFirstDocument] = useState<unknown>()
  const getDataMode = 'lazyLoading' as const
  const [data, setData] = useState<DataSection[]>([])
  const [secondData, setSecondData] = useState<DataSection[]>([])
  const [store, setStore] = useState<string>(preselectedStore ? preselectedStore : (showStoreFilter ? (userStoreId || 'all') : 'all'))
  const [range, setRange] = useState<DateRange | 'allTime'>({ startDate: null, endDate: null })
  const [totalSum, setTotalSum] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isBottomLoading, setIsBottomLoading] = useState<boolean>(false)
  const [listRefreshing, setListRefreshing] = useState<boolean>(false)
  // ATL-986: set when a declared filter cannot be applied on the active read path.
  // While set we render an error instead of the list — an unfiltered list of
  // operations is wrong money on screen, which is worse than no list at all.
  const [filterError, setFilterError] = useState<string | null>(null)
  const _rangePicker = React.createRef<{ blur: () => void }>()
  const unsubscribePreviousPeriods = useRef<(() => void) | undefined>(undefined)
  const unsubscribeSecondPreviousPeriods = useRef<(() => void) | undefined>(undefined)
  const localeDateService = useAtomValue(localeDateServiceAtom)
  // ATL-1445 stage 3 P6: this device's unsent rows of this collection (the local view). `null` for
  // every session today, and then the list renders `data` itself — the same object as before.
  const localRows = useAtomValue(localRowsOverlayAtom)
  const pendingRows = pendingRowsForList(localRows, collectionName)
  // The filter the rows on screen were read with. Pending rows cannot be judged against a declared
  // filter (ATL-986: an unfiltered row is wrong money on screen), so they are merged only into an
  // unfiltered list.
  const readFilterRef = useRef<OperationsFilter | null | undefined>(additionalFilters)

  // Export handler function
  const handleExport = async (type: 'csv' | 'excel') => {
    if (!exportDataTransformer || data.length === 0) return

    try {
      // Flatten all items from all sections
      const allItems: DataItem[] = data.flatMap(section => section.data)

      // Transform data using the provided transformer
      const exportData = exportDataTransformer(allItems, t)

      if (exportData.length === 0) return

      // Generate filename with date range
      let startDateStr = 'all'
      let endDateStr = 'time'
      if (range !== 'allTime') {
        const rangeObj = range as DateRange
        startDateStr = rangeObj.startDate ? format(rangeObj.startDate, 'd-M-y') : 'all'
        endDateStr = rangeObj.endDate ? format(rangeObj.endDate, 'd-M-y') : 'time'
      }
      const filename = `${exportFilenamePrefix || collectionName}-${startDateStr}-${endDateStr}`

      if (type === 'csv') {
        await exportToCSV(exportData, filename)
      } else {
        await exportToExcel(exportData, filename)
      }
    } catch (error) {
      console.error('Export failed:', error)
    }
  }

  useImperativeHandle(ref, () => {
    return {
      refresh(passedRange?: DateRange, additionalFiltersArg?: OperationsFilter | null) {
        // `undefined` means "keep the declared filter"; an explicit `null` means
        // "no filter" and must NOT fall back to the declared one.
        const effectiveFilter = additionalFiltersArg === undefined ? additionalFilters : additionalFiltersArg
        if (passedRange) {
          setRange(passedRange)
          void getData(passedRange, store, false, effectiveFilter)
        } else {
          void getData(range, store, false, effectiveFilter)
        }
      }
    }
  }, [range, store])

  useEffect(() => {
    void getData(range, store, false, additionalFilters)
  }, [secondQuery])


  useEffect(() => {
    if (showAddButton) {
      navigation.setOptions({
        headerRight: () => (
          <HeaderIconButton
            onPress={() => {
              navigation.navigate(detailScreenName!, { ...detailScreenParams, detail: null, addItemToData, preselectedStore: store })
            }}
            iconName='plus-outline'
            testID='operations-add-button'
          />
        ),
      })
    }
  }, [store, data?.length, (range as DateRange).startDate, (range as DateRange).endDate, lastDocument, firstDocument])

  useEffect(() => {
    const localStoreNow = getLocalStoreDate()
    void getData(initialRange ? initialRange : { startDate: localStoreNow, endDate: localStoreNow }, store, false, additionalFilters)
    setRange(initialRange ? initialRange : { startDate: localStoreNow, endDate: localStoreNow })
    return () => {
      unsubscribePreviousPeriods?.current?.()
      unsubscribeSecondPreviousPeriods?.current?.()
    }
  }, [])

  // ATL-1445 stage 3 P6: the rows shown = the server's rows + this device's pending rows in view,
  // de-duplicated by id (the server row wins). `data` itself when there are none.
  const displayData = useMemo<DataSection[]>(() => {
    if (pendingRows === null || pendingRows.length === 0 || readFilterRef.current != null) return data
    let startMs: number | null = null
    let endMs: number | null = null
    if (range !== 'allTime') {
      if (range.startDate == null || range.endDate == null) return data
      const storeIdForRange = store !== 'all' ? store : (userStoreId || null)
      const localStoreRange = dateRangeWithTimezoneAndAutoclose(range, storeIdForRange ? [storeIdForRange] : null)
      startMs = localStoreRange.startDate ? localStoreRange.startDate.getTime() : null
      endMs = localStoreRange.endDate ? localStoreRange.endDate.getTime() : null
    }
    return mergePendingRows(data, pendingRows, {
      store,
      startMs,
      endMs,
      dateField,
      localDateOf: (rowStore, at) => getLocalStoreDate(rowStore, at),
    })
  }, [data, pendingRows, range, store, userStoreId, dateField])

  useEffect(() => {
    if (showTotalSum && totalSumField) {
      let newTotalSum = 0
      // ATL-1445 stage 3 P6: over the rows SHOWN, pending ones included (`displayData` is `data` itself
      // when there are none — every list today).
      for (const day of displayData) {
        for (const item of day.data) {
          newTotalSum += (item[totalSumField] as number) || 0
        }
      }
      setTotalSum(newTotalSum)
    }
    // Review 1 nit (c): also when this device's pending rows change — a pending row merged into an
    // EXISTING section does not change the section count. `pendingRows` is `null` for every list today,
    // so today's triggers are master's (`data?.length`; its own weakness is left as it is).
  }, [displayData?.length, pendingRows])

  const mergeCollectionsData = (freshFirstData: DataSection[], freshSecondData: DataSection[]): void => {
    if (!secondQuery || !freshSecondData?.length) return

    // Create a copy of the primary data
    const mergedData = [...freshFirstData]

    // Process each section in the second data collection
    for (const secondSection of freshSecondData) {
      // Find matching section in primary data by date
      const primarySectionIndex = mergedData.findIndex(section => section.title === secondSection.title)

      if (primarySectionIndex !== -1) {
        // Section exists in primary data, merge items
        for (const item of secondSection.data) {
          // Apply the merger function if provided to determine how items should be merged
          if (secondQuery.merger) {
            const mergedItems = secondQuery.merger(mergedData[primarySectionIndex].data, item)
            mergedData[primarySectionIndex].data = mergedItems
          } else {
            // Default behavior: just append items from second collection
            mergedData[primarySectionIndex].data.push({
              ...item,
              isFromSecondCollection: true
            })
          }
        }

        // Sort by date if needed
        if (secondQuery.sortMergedData) {
          mergedData[primarySectionIndex].data.sort((a, b) => {
            // Determine which date field to use for each item
            const aDateField = a.isFromSecondCollection ? (secondQuery.dateField || dateField) : dateField
            const bDateField = b.isFromSecondCollection ? (secondQuery.dateField || dateField) : dateField

            // Get date values accounting for different formats
            let dateA: Date
            let dateB: Date

            // Get date A
            const aDateValue = a[aDateField]
            if (aDateValue && typeof aDateValue === 'object' && 'toDate' in aDateValue) {
              dateA = (aDateValue as FirestoreTimestamp).toDate()
            } else if (aDateValue instanceof Date) {
              dateA = aDateValue
            } else if (typeof aDateValue === 'number') {
              dateA = new Date(aDateValue)
            } else {
              dateA = new Date() // Fallback
            }

            // Get date B
            const bDateValue = b[bDateField]
            if (bDateValue && typeof bDateValue === 'object' && 'toDate' in bDateValue) {
              dateB = (bDateValue as FirestoreTimestamp).toDate()
            } else if (bDateValue instanceof Date) {
              dateB = bDateValue
            } else if (typeof bDateValue === 'number') {
              dateB = new Date(bDateValue)
            } else {
              dateB = new Date() // Fallback
            }

            return dateB.getTime() - dateA.getTime() // Descending order
          })
        }
      } else {
        // Section doesn't exist in primary data, add it
        // Mark all items in this section as coming from second collection
        const itemsWithFlag = secondSection.data.map(item => ({
          ...item,
          isFromSecondCollection: true
        }))

        mergedData.push({
          ...secondSection,
          data: itemsWithFlag,
          index: mergedData.length
        })
      }
    }

    // Sort sections by date
    mergedData.sort((a, b) => {
      const dateA = parse(a.title, "d/M/y EEEE", new Date()).getTime()
      const dateB = parse(b.title, "d/M/y EEEE", new Date()).getTime()
      return dateB - dateA // Descending order
    })

    // Reindex sections
    mergedData.forEach((section, index) => {
      section.index = index
    })

    setData(mergedData)
  }

  const addItemToData = (newItem: DataItem, itemDate?: Date, itemStore?: string): void => {
    // find appropriate index, if date or store is not seen - then don't add
    if (itemStore !== store && store !== 'all') return
    setData(currentData => {
      const localStoreDate = getLocalStoreDate(itemStore, (itemDate || new Date()))
      const sectionTitle = format(localStoreDate, "d/M/y EEEE")
      const sectionIndex = currentData.findIndex(section => section.title === sectionTitle)
      if (sectionIndex === -1) { // there is no existing section to insert the new item
        let localStoreRange: DateRange | undefined
        if (range !== 'allTime') {
          localStoreRange = dateRangeWithTimezoneAndAutoclose(range)
        }
        if (range === 'allTime' || (localStoreRange?.startDate && localStoreRange?.endDate && localStoreDate.getTime() >= localStoreRange.startDate.getTime() && localStoreDate.getTime() <= localStoreRange.endDate.getTime())) {
          // Item date is within the range, proceed with adding the item
          const newSection: DataSection = {
            title: sectionTitle,
            data: [newItem],
            index: 0
          }
          const timestamp = parse(sectionTitle, "d/M/y EEEE", new Date()).getTime()
          const insertIndex = currentData.findIndex(section => parse(section.title, "d/M/y EEEE", new Date()).getTime() < timestamp)
          if (insertIndex === -1) {
            // If no later date is found, add the new section at the end
            if (lastDocument === 'end') {
              if (!firstDocument) {
                setFirstDocument(newItem)
              }
              return [...currentData, { ...newSection, index: currentData.length }]
            } else { // no need to add cause it will appear on lazy load at scroll end
              return currentData
            }
          } else {
            // Insert the new section at the found index
            const newData = [...currentData]
            newData.splice(insertIndex, 0, { ...newSection, index: insertIndex })
            newData.forEach((section, idx) => {
              section.index = idx
            })
            return newData
          }
        } else {
          // Item date is not within the range, do not add the item
          return currentData
        }
      } else {
        const newData = [...currentData]
        newData[sectionIndex].data.splice(0, 0, newItem)
        if (sectionIndex === 0) {
          setFirstDocument(newItem)
        }
        return newData
      }
    })
  }

  const editItemInData = (modifiedItem: DataItem, sectionIndex: number, itemIndex: number): void => {
    const newData = [...data]
    newData[sectionIndex].data.splice(itemIndex, 1, modifiedItem)
    setData(newData)
  }

  const removeItemFromData = (sectionIndex: number, itemIndex: number): void => {
    setData(currentData => {
      const newData = [...currentData]
      newData[sectionIndex].data.splice(itemIndex, 1)
      return newData
    })
  }

  // Transform API response to match Firestore format
  const transformApiItemToFirestoreFormat = (item: ApiResponse, apiDateField: string): DataItem => {
    // Start with the item data - spread first, then override id to avoid duplicate property warning
    const transformed: DataItem = {
      ...item,
      id: item.id || (item as { firestore_id?: string }).firestore_id || '',
    } as DataItem

    // Convert date to Firestore Timestamp-like object
    // The TypedApiClient may have already converted dates to TimestampLike objects
    const dateValue = (item as Record<string, unknown>)[apiDateField]
    if (dateValue) {
      let parsedDate: Date

      // Check if it's already a TimestampLike object (from TypedApiClient's deserializeDates)
      if (typeof dateValue === 'object' && dateValue !== null && 'toDate' in dateValue && typeof (dateValue as { toDate: unknown }).toDate === 'function') {
        parsedDate = (dateValue as { toDate: () => Date }).toDate()
      } else if (typeof dateValue === 'string') {
        parsedDate = new Date(dateValue)
      } else {
        parsedDate = new Date()
      }

      // Create a Firestore Timestamp-like object for compatibility — with `seconds`/`nanoseconds`,
      // which is what makes the save path read it as a Timestamp (ATL-1966, utils/timestampLike).
      // Use the Firestore dateField name (e.g., 'saleDate') not the API field name
      transformed[dateField] = timestampLikeOf(parsedDate)
    }

    // Convert snake_case fields to camelCase for compatibility with Firestore format
    const itemAny = item as Record<string, unknown>
    if (itemAny.store_id !== undefined) {
      transformed.store = itemAny.store_id as string
    }

    // Convert cashier_uid to cashierUID (use Firebase UID for user lookup)
    // cashier_uid is the Firebase Auth UID, cashier_id is the Supabase UUID
    if (itemAny.cashier_uid !== undefined) {
      transformed.cashierUID = itemAny.cashier_uid
    } else if (itemAny.cashier_id !== undefined) {
      // Fallback to cashier_id if cashier_uid is not available
      transformed.cashierUID = itemAny.cashier_id
    }

    // Convert cart_items to cartItems
    if (itemAny.cart_items !== undefined) {
      transformed.cartItems = itemAny.cart_items
    }

    // Convert total_price to finalTotalCartPrice (for sales)
    if (itemAny.total_price !== undefined && collectionName === 'sales') {
      transformed.finalTotalCartPrice = itemAny.total_price
    }

    // Convert payment_method to paymentMethod (handle both string and object formats)
    if (itemAny.payment_method !== undefined) {
      if (typeof itemAny.payment_method === 'string') {
        // If it's a string, try to find the payment method object or create a simple one
        transformed.paymentMethod = itemAny.payment_method
      } else if (itemAny.payment_method && typeof itemAny.payment_method === 'object') {
        transformed.paymentMethod = itemAny.payment_method
      }
    }

    // Convert second_payment_method to secondPaymentMethod
    if (itemAny.second_payment_method !== undefined) {
      transformed.secondPaymentMethod = itemAny.second_payment_method
    }

    // Convert purchase_method to purchaseMethod
    if (itemAny.purchase_method !== undefined) {
      if (typeof itemAny.purchase_method === 'string') {
        transformed.purchaseMethod = itemAny.purchase_method
      } else if (itemAny.purchase_method && typeof itemAny.purchase_method === 'object') {
        transformed.purchaseMethod = itemAny.purchase_method
      }
    }

    // Convert client_id to client object if needed (for now just keep client_id)
    // Note: client object might need to be fetched separately
    if (itemAny.client_id !== undefined && !itemAny.client) {
      transformed.clientId = itemAny.client_id
    }

    // Keep firestore_id if present for reference
    if (itemAny.firestore_id) {
      transformed.firestore_id = itemAny.firestore_id as string
    }

    // ATL-1966: a supply order's supplier key arrives as `supplierKey`, so that released bundles
    // never open its form; this bundle reads it where the detail screen does, `supplierID`.
    if (collectionName === 'supplyOrders') {
      // `Omit` over DataItem's index signature drops its declared keys, so `id` is restated.
      return {...withSupplyOrderSupplierKey(transformed), id: transformed.id}
    }

    return transformed
  }

  // Fetch data from Supabase API
  const getPaginatedDataFromSupabase = async (
    selectedRange: DateRange | 'allTime',
    selectedStore: string = store,
    previousLastDocument: unknown,
    previousData: DataSection[],
    isRefresh: boolean,
    previousFirstDocument: unknown,
    additionalFiltersArg?: OperationsFilter | null
  ): Promise<DataSection[]> => {
    const apiEndpoint = COLLECTION_TO_API_ENDPOINT[collectionName]
    if (!apiEndpoint) {
      console.warn(`No API endpoint mapping for collection: ${collectionName}`)
      return previousData || []
    }

    const apiDateField = getApiDateField(collectionName, dateField)

    let localStoreRange: DateRange | undefined
    if (selectedRange !== 'allTime') {
      // For "all stores", use the user's own store timezone for the date range.
      // dateRangeWithTimezoneAndAutoclose's multi-store offset iteration fails
      // when stores have edge-case timezone data (offset 0, missing utc arrays).
      // Using the user's store gives correct results and matches Firestore behavior.
      const storeIdForRange = selectedStore !== 'all' ? selectedStore : (userStoreId || null)
      localStoreRange = dateRangeWithTimezoneAndAutoclose(
        selectedRange,
        storeIdForRange ? [storeIdForRange] : null
      )
    }

    try {
      // Calculate offset based on current data length
      const currentItemCount = previousData?.reduce((sum, section) => sum + section.data.length, 0) || 0

      // Set loading state to prevent concurrent fetches (only for pagination, not refresh)
      if (!isRefresh && currentItemCount > 0) {
        setIsBottomLoading(true)
      }

      // ATL-986: the caller's filter is applied HERE or the request is not made at
      // all. `buildSupabaseListParams` returns null when the declared filter has no
      // API equivalent; issuing the request anyway would return every row in the
      // collection, which is what this list used to do.
      const resolvedFilter = resolveOperationsFilter(additionalFiltersArg)
      if (!resolvedFilter.ok) {
        console.error(`OperationsList (${collectionName}): ${resolvedFilter.reason}`)
        setFilterError(resolvedFilter.reason)
        setData([])
        setIsLoading(false)
        setIsBottomLoading(false)
        setListRefreshing(false)
        return []
      }
      setFilterError(null)

      const params = buildSupabaseListParams({
        limit: 10,
        offset: isRefresh ? 0 : currentItemCount,
        storeId: selectedStore,
        startDate: selectedRange !== 'allTime' && localStoreRange?.startDate
          ? localStoreRange.startDate.toISOString()
          : null,
        endDate: selectedRange !== 'allTime' && localStoreRange?.endDate
          ? localStoreRange.endDate.toISOString()
          : null,
        filter: additionalFiltersArg,
      })
      // `resolvedFilter.ok` above already guarantees a non-null result; this keeps
      // the narrowing explicit rather than asserting it away.
      if (params === null) return []

      // Try typed API first, fall back to untyped for unsupported collections
      const typedFetcher = getTypedApiFetcher(collectionName)
      let apiData: ApiResponse[]
      if (typedFetcher) {
        // Typed API returns data directly (already validated)
        apiData = await typedFetcher(params)
      } else {
        // Fall back to untyped API for unsupported collections (e.g., orders)
        const response = await api.get(`/${apiEndpoint}`, { params })
        apiData = Array.isArray(response.data) ? response.data : []
      }

      if (apiData.length === 0) {
        setLastDocument('end')
        // On refresh/filter change (previousData is empty), show empty list
        // On pagination (previousData has items), keep existing data
        const dataToSet = (previousData && previousData.length > 0) ? previousData : []
        setData(dataToSet)
        setIsLoading(false)
        setIsBottomLoading(false)
        setListRefreshing(false)
        return dataToSet
      }

      // Transform API data to match Firestore format
      const transformedData = apiData.map(item => transformApiItemToFirestoreFormat(item, apiDateField))

      // Group by date (same logic as Firestore version)
      const newDataDict: Record<string, DataItem[]> = {}
      // Only include previousData when paginating (not refreshing)
      // On refresh, we want fresh data, not duplicates
      if (!isRefresh) {
        previousData?.forEach(item => {
          newDataDict[item.title] = item.data
        })
      }

      transformedData.forEach(snap => {
        const dateValue = snap[dateField]
        const snapDate = dateValue && typeof dateValue === 'object' && 'toDate' in dateValue
          ? (dateValue as FirestoreTimestamp).toDate()
          : new Date()
        const date = format(getLocalStoreDate(snap.store, snapDate), "d/M/y EEEE")
        const newArr = newDataDict[date] ? [...newDataDict[date]] : []
        newArr.push(snap)
        newDataDict[date] = [...newArr]
      })

      const newDataArray: DataSection[] = []
      let sectionIndex = 0
      for (const key in newDataDict) {
        const newData: DataSection = {
          title: key,
          data: newDataDict[key],
          index: sectionIndex
        }
        newDataArray.push(newData)
        sectionIndex += 1
      }

      // Sort sections by date (descending)
      newDataArray.sort((a, b) => {
        const dateA = parse(a.title, "d/M/y EEEE", new Date()).getTime()
        const dateB = parse(b.title, "d/M/y EEEE", new Date()).getTime()
        return dateB - dateA
      })

      // Reindex after sorting
      newDataArray.forEach((section, index) => {
        section.index = index
      })

      if (apiData.length < 10) {
        setLastDocument('end')
      } else {
        // For pagination, we'll use the last item's ID
        setLastDocument(transformedData[transformedData.length - 1])
      }

      if (isRefresh || previousFirstDocument === undefined) {
        setFirstDocument(transformedData[0])
      }

      setData(newDataArray)
      setIsLoading(false)
      setIsBottomLoading(false)
      setListRefreshing(false)

      if (additionalDataValidation) {
        additionalDataValidation(newDataArray, localStoreRange, selectedStore)
      }

      return newDataArray
    } catch (error) {
      console.error('Error fetching data from Supabase API:', error)
      setIsLoading(false)
      setIsBottomLoading(false)
      setListRefreshing(false)
      // Fallback to empty data on error
      return previousData || []
    }
  }

  /**
   * Apply a declared filter to a Firestore query.
   *
   * Firestore CAN honour a closure the Supabase path cannot express, so both the
   * deliberate `api: null` case and the malformed pre-ATL-986 shape still produce
   * correct data here. Only the malformed one is reported: a caller that declared
   * `api: null` on purpose (a Firestore-only collection) is not a defect, and logging
   * it on every fetch would bury the case that is.
   */
  const applyFirestoreFilter = (query: FirestoreQuery, filterValue: unknown): FirestoreQuery => {
    const resolved = resolveOperationsFilter(filterValue)
    if (resolved.ok) {
      return resolved.firestore ? resolved.firestore(query) : query
    }
    if (resolved.kind === 'invalid') {
      console.error(`OperationsList (${collectionName}): ${resolved.reason}`)
    }
    if (resolved.firestoreUsable && resolved.firestore) {
      return resolved.firestore(query)
    }
    return query
  }

  const getData = async (
    selectedRange: DateRange | 'allTime' = range,
    selectedStore: string = store,
    isRefresh: boolean = false,
    additionalFiltersArg?: OperationsFilter | null
  ): Promise<void> => {
    const rangeObj = selectedRange as DateRange
    if ((rangeObj.startDate != null && rangeObj.endDate != null) || selectedRange === 'allTime') {
      _rangePicker.current?.blur()
      readFilterRef.current = additionalFiltersArg

      // Use Supabase API if enabled
      if (useSupabase && COLLECTION_TO_API_ENDPOINT[collectionName]) {
        if (getDataMode === 'lazyLoading') {
          let newLastDocument = lastDocument
          let newFirstDocument = firstDocument
          let newData = [...data]

          if (selectedStore !== store
            || (selectedRange === 'allTime' && selectedRange !== range)
            || (selectedRange !== 'allTime' && range !== 'allTime' && !isEqual(rangeObj.startDate!, (range as DateRange).startDate!))
            || (selectedRange !== 'allTime' && range !== 'allTime' && !isEqual(rangeObj.endDate!, (range as DateRange).endDate!))) {
            newLastDocument = undefined
            newFirstDocument = undefined
            newData = []
            setIsLoading(true)
          }

          if (newLastDocument !== 'end' || isRefresh) {
            await getPaginatedDataFromSupabase(selectedRange, selectedStore, newLastDocument, newData, isRefresh, newFirstDocument, additionalFiltersArg)
          }
          setIsLoading(false)
        } else {
          // Subscribe mode not supported for Supabase - fall back to lazy loading
          console.warn('Subscribe mode not supported for Supabase, using lazy loading')
          setIsLoading(true)
          await getPaginatedDataFromSupabase(selectedRange, selectedStore, undefined, [], false, undefined, additionalFiltersArg)
          setIsLoading(false)
        }
        return
      }

      // Use Firestore (original implementation)
      if (getDataMode === 'lazyLoading') {
        let newLastDocument = lastDocument
        let newFirstDocument = firstDocument
        let newData = [...data]
        let newSecondLastDocument = secondLastDocument
        let newSecondFirstDocument = secondFirstDocument
        let newSecondData = [...secondData]

        if (selectedStore !== store
          || (selectedRange === 'allTime' && selectedRange !== range)
          || (selectedRange !== 'allTime' && range !== 'allTime' && !isEqual(rangeObj.startDate!, (range as DateRange).startDate!))
          || (selectedRange !== 'allTime' && range !== 'allTime' && !isEqual(rangeObj.endDate!, (range as DateRange).endDate!))) {
          newLastDocument = undefined
          newFirstDocument = undefined
          newData = []
          newSecondLastDocument = undefined
          newSecondFirstDocument = undefined
          newSecondData = []
          setIsLoading(true)
        }

        let freshFirstData: DataSection[] | undefined
        if (newLastDocument !== 'end' || isRefresh) {
          freshFirstData = await getPaginatedDataFromFB(selectedRange, selectedStore, newLastDocument, newData, isRefresh, newFirstDocument, additionalFiltersArg)
        }
        // Fetch data from second collection if secondQuery is provided
        if (secondQuery) {
          // Only fetch second data if we haven't reached the end
          setIsLoading(true)
          if (newSecondLastDocument !== 'end' || isRefresh) {
            const freshSecondData = await getSecondPaginatedDataFromFB(selectedRange, selectedStore, newSecondLastDocument, newSecondData, isRefresh, newSecondFirstDocument)
            if (freshFirstData || freshSecondData) {
              mergeCollectionsData(freshFirstData ? freshFirstData : newData, freshSecondData ? freshSecondData : newSecondData)
            }
          }
        }
        setIsLoading(false)
      } else if (getDataMode === 'subscribe') {
        await subscribeToDataFromFB(selectedRange, selectedStore)

        // Subscribe to second collection if secondQuery is provided
        if (secondQuery) {
          await subscribeToSecondDataFromFB(selectedRange, selectedStore)
        }
      }
    }
  }

  const subscribeToDataFromFB = async (selectedRange: DateRange | 'allTime', selectedStore: string = store): Promise<void> => {
    setIsLoading(true)
    unsubscribePreviousPeriods?.current?.()
    setData([])
    let localStoreRange: DateRange | undefined
    if (selectedRange !== 'allTime') {
      localStoreRange = dateRangeWithTimezoneAndAutoclose(selectedRange)
    }
    let newUnsubscribePreviousPeriods = db.collection(collectionName)
    if (selectedStore !== 'all') {
      newUnsubscribePreviousPeriods = newUnsubscribePreviousPeriods.where('store', '==', selectedStore)
      if (selectedRange !== 'allTime') {
        localStoreRange = dateRangeWithTimezoneAndAutoclose(selectedRange, [selectedStore])
      }
    }
    newUnsubscribePreviousPeriods = applyFirestoreFilter(newUnsubscribePreviousPeriods, additionalFilters)
    if (selectedRange !== 'allTime' && localStoreRange?.startDate && localStoreRange?.endDate) {
      newUnsubscribePreviousPeriods = newUnsubscribePreviousPeriods.where(dateField, '>=', localStoreRange.startDate).where(dateField, '<=', localStoreRange.endDate).orderBy(dateField, 'desc')
    } else {
      newUnsubscribePreviousPeriods = newUnsubscribePreviousPeriods.orderBy(dateField, 'desc')
    }
    newUnsubscribePreviousPeriods = newUnsubscribePreviousPeriods.onSnapshot(function (querySnapshot: { forEach: (callback: (doc: { id: string; ref: unknown; data: () => Record<string, unknown> }) => void) => void }) {
      const docs: Record<string, DataItem[]> = {} // date key - array of sales
      querySnapshot?.forEach(function (doc) {
        const snap: DataItem = doc.data() as DataItem
        snap.id = doc.id
        snap.ref = doc.ref
        const dateValue = snap[dateField]
        const snapDate = dateValue && typeof dateValue === 'object' && 'toDate' in dateValue
          ? (dateValue as FirestoreTimestamp).toDate()
          : new Date()
        const date = format(getLocalStoreDate(snap.store, snapDate), "d/M/y EEEE")
        const newArr = docs[date] ? [...docs[date]] : []
        newArr.push(snap)
        docs[date] = [...newArr]
      })
      const docsArr: DataSection[] = []
      let sectionIndex = 0
      for (const key in docs) {
        const newData: DataSection = {
          title: key,
          data: docs[key],
          index: sectionIndex
        }
        docsArr.push(newData)
        sectionIndex += 1
      }
      setData(docsArr)
      setIsLoading(false)
      additionalDataValidation?.(docsArr, localStoreRange, selectedStore)
    })
    unsubscribePreviousPeriods.current = newUnsubscribePreviousPeriods
  }

  const subscribeToSecondDataFromFB = async (selectedRange: DateRange | 'allTime', selectedStore: string = store): Promise<void> => {
    unsubscribeSecondPreviousPeriods?.current?.()
    setSecondData([])

    let localStoreRange: DateRange | undefined
    if (selectedRange !== 'allTime') {
      localStoreRange = dateRangeWithTimezoneAndAutoclose(selectedRange)
    }

    let newUnsubscribe = db.collection(secondQuery!.collectionName)
    if (selectedStore !== 'all') {
      newUnsubscribe = newUnsubscribe.where('store', '==', selectedStore)
      if (selectedRange !== 'allTime') {
        localStoreRange = dateRangeWithTimezoneAndAutoclose(selectedRange, [selectedStore])
      }
    }

    newUnsubscribe = secondQuery!.additionalFilters ? secondQuery!.additionalFilters(newUnsubscribe) : newUnsubscribe

    const secondDateField = secondQuery!.dateField || dateField
    if (selectedRange !== 'allTime' && localStoreRange?.startDate && localStoreRange?.endDate) {
      newUnsubscribe = newUnsubscribe.where(secondDateField, '>=', localStoreRange.startDate)
        .where(secondDateField, '<=', localStoreRange.endDate)
        .orderBy(secondDateField, 'desc')
    } else {
      newUnsubscribe = newUnsubscribe.orderBy(secondDateField, 'desc')
    }

    newUnsubscribe = newUnsubscribe.onSnapshot(function (querySnapshot: { forEach: (callback: (doc: { id: string; ref: unknown; data: () => Record<string, unknown> }) => void) => void }) {
      const docs: Record<string, DataItem[]> = {} // date key - array of items
      querySnapshot?.forEach(function (doc) {
        const snap: DataItem = doc.data() as DataItem
        snap.id = doc.id
        snap.ref = doc.ref
        const dateValue = snap[secondDateField]
        const snapDate = dateValue && typeof dateValue === 'object' && 'toDate' in dateValue
          ? (dateValue as FirestoreTimestamp).toDate()
          : new Date()
        const date = format(getLocalStoreDate(snap.store, snapDate), "d/M/y EEEE")
        const newArr = docs[date] ? [...docs[date]] : []
        newArr.push(snap)
        docs[date] = [...newArr]
      })

      const docsArr: DataSection[] = []
      let sectionIndex = 0
      for (const key in docs) {
        const newData: DataSection = {
          title: key,
          data: docs[key],
          index: sectionIndex
        }
        docsArr.push(newData)
        sectionIndex += 1
      }

      setSecondData(docsArr)
    })

    unsubscribeSecondPreviousPeriods.current = newUnsubscribe
  }

  const getPaginatedDataFromFB = async (
    selectedRange: DateRange | 'allTime',
    selectedStore: string = store,
    previousLastDocument: unknown,
    previousData: DataSection[],
    isRefresh: boolean,
    previousFirstDocument: unknown,
    additionalFiltersArg?: OperationsFilter | null
  ): Promise<DataSection[]> => {
    let localStoreRange: DateRange | undefined
    if (selectedRange !== 'allTime') {
      localStoreRange = dateRangeWithTimezoneAndAutoclose(selectedRange)
    }
    let newRefForPreviousPeriods = db.collection(collectionName)
    if (selectedStore !== 'all') {
      newRefForPreviousPeriods = newRefForPreviousPeriods.where('store', '==', selectedStore)
      if (selectedRange !== 'allTime') {
        localStoreRange = dateRangeWithTimezoneAndAutoclose(selectedRange, [selectedStore])
      }
    }
    newRefForPreviousPeriods = applyFirestoreFilter(newRefForPreviousPeriods, additionalFiltersArg)
    if (selectedRange !== 'allTime' && localStoreRange?.startDate && localStoreRange?.endDate) {
      newRefForPreviousPeriods = newRefForPreviousPeriods.where(dateField, '>=', localStoreRange.startDate).where(dateField, '<=', localStoreRange.endDate).orderBy(dateField, 'desc')
    } else {
      newRefForPreviousPeriods = newRefForPreviousPeriods.orderBy(dateField, 'desc')
    }
    if (isRefresh) {
      if (previousFirstDocument !== undefined) {
        newRefForPreviousPeriods = newRefForPreviousPeriods.endBefore(previousFirstDocument)
      }
    } else {
      if (previousLastDocument !== undefined) {
        setIsBottomLoading(true)
        newRefForPreviousPeriods = newRefForPreviousPeriods.startAfter(previousLastDocument) // fetch data following the last document accessed
      }
      if (!secondQuery) {
        newRefForPreviousPeriods = newRefForPreviousPeriods.limit(10)
      }
    }
    const querySnapshot = await newRefForPreviousPeriods.get()
    if (querySnapshot.docs.length > 0) {
      if (!secondQuery) {
        setLastDocument(querySnapshot.docs[querySnapshot.docs.length - 1])
      } else {
        setLastDocument('end')
      }
      if (isRefresh || previousFirstDocument === undefined) {
        setFirstDocument(querySnapshot.docs[0])
      }
      if (querySnapshot.docs.length < 10) {
        if (!isRefresh) {
          setLastDocument('end') // nothing to load more
        }
      }
    } else {
      if (!isRefresh) {
        setLastDocument('end') // nothing to load more
      }
    }
    const newDataDict: Record<string, DataItem[]> = {}
    previousData.forEach(item => {
      newDataDict[item.title] = item.data
    })
    let index = 0
    querySnapshot?.forEach(function (doc: { id: string; ref: unknown; data: () => Record<string, unknown> }) {
      const snap: DataItem = doc.data() as DataItem
      snap.id = doc.id
      snap.ref = doc.ref
      const dateValue = snap[dateField]
      const snapDate = dateValue && typeof dateValue === 'object' && 'toDate' in dateValue
        ? (dateValue as FirestoreTimestamp).toDate()
        : new Date()
      const date = format(getLocalStoreDate(snap.store, snapDate), "d/M/y EEEE")
      const newArr = newDataDict[date] ? [...newDataDict[date]] : []
      if (isRefresh) {
        newArr.splice(index, 0, snap)
        index += 1
      } else {
        newArr.push(snap)
      }
      newDataDict[date] = [...newArr]
    })
    const newDataArray: DataSection[] = []
    let sectionIndex = 0
    for (const key in newDataDict) {
      const newData: DataSection = {
        title: key,
        data: newDataDict[key],
        index: sectionIndex
      }
      newDataArray.push(newData)
      sectionIndex += 1
    }
    setData(newDataArray)
    setIsLoading(false)
    setIsBottomLoading(false)
    setListRefreshing(false)
    additionalDataValidation?.(newDataArray, localStoreRange, selectedStore)
    return newDataArray
  }

  const getSecondPaginatedDataFromFB = async (
    selectedRange: DateRange | 'allTime',
    selectedStore: string = store,
    previousLastDocument: unknown,
    previousData: DataSection[],
    isRefresh: boolean,
    previousFirstDocument: unknown
  ): Promise<DataSection[] | undefined> => {
    if (!secondQuery) return undefined

    let localStoreRange: DateRange | undefined
    if (selectedRange !== 'allTime') {
      localStoreRange = dateRangeWithTimezoneAndAutoclose(selectedRange)
    }

    let newRefForPreviousPeriods = db.collection(secondQuery.collectionName)

    if (selectedStore !== 'all') {
      newRefForPreviousPeriods = newRefForPreviousPeriods.where('store', '==', selectedStore)
      if (selectedRange !== 'allTime') {
        localStoreRange = dateRangeWithTimezoneAndAutoclose(selectedRange, [selectedStore])
      }
    }

    newRefForPreviousPeriods = secondQuery.additionalFilters ?
      secondQuery.additionalFilters(newRefForPreviousPeriods) : newRefForPreviousPeriods

    const secondDateField = secondQuery.dateField || dateField
    if (selectedRange !== 'allTime' && localStoreRange?.startDate && localStoreRange?.endDate) {
      newRefForPreviousPeriods = newRefForPreviousPeriods
        .where(secondDateField, '>=', localStoreRange.startDate)
        .where(secondDateField, '<=', localStoreRange.endDate)
        .orderBy(secondDateField, 'desc')
    } else {
      newRefForPreviousPeriods = newRefForPreviousPeriods.orderBy(secondDateField, 'desc')
    }

    if (isRefresh) {
      if (previousFirstDocument !== undefined) {
        newRefForPreviousPeriods = newRefForPreviousPeriods.endBefore(previousFirstDocument)
      }
    } else {
      if (previousLastDocument !== undefined) {
        newRefForPreviousPeriods = newRefForPreviousPeriods.startAfter(previousLastDocument)
      }
    }

    const querySnapshot = await newRefForPreviousPeriods.get()
    if (querySnapshot.docs.length > 0) {
      setSecondLastDocument(querySnapshot.docs[querySnapshot.docs.length - 1])
      if (isRefresh || previousFirstDocument === undefined) {
        setSecondFirstDocument(querySnapshot.docs[0])
      }
      setSecondLastDocument('end')
    } else if (!isRefresh) {
      setSecondLastDocument('end')
    }

    const newDataDict: Record<string, DataItem[]> = {}
    previousData.forEach(item => {
      newDataDict[item.title] = item.data
    })

    let index = 0
    querySnapshot?.forEach(function (doc: { id: string; ref: unknown; data: () => Record<string, unknown> }) {
      const snap: DataItem = doc.data() as DataItem
      snap.id = doc.id
      snap.ref = doc.ref
      const dateValue = snap[secondDateField]
      const snapDate = dateValue && typeof dateValue === 'object' && 'toDate' in dateValue
        ? (dateValue as FirestoreTimestamp).toDate()
        : new Date()
      const date = format(getLocalStoreDate(snap.store, snapDate), "d/M/y EEEE")
      const newArr = newDataDict[date] ? [...newDataDict[date]] : []
      if (isRefresh) {
        newArr.splice(index, 0, snap)
        index += 1
      } else {
        newArr.push(snap)
      }
      newDataDict[date] = [...newArr]
    })

    const newDataArray: DataSection[] = []
    let sectionIndex = 0
    for (const key in newDataDict) {
      const newData: DataSection = {
        title: key,
        data: newDataDict[key],
        index: sectionIndex
      }
      newDataArray.push(newData)
      sectionIndex += 1
    }

    setSecondData(newDataArray)
    return newDataArray
  }

  // While this list is mounted an acknowledged pending row stays until the list shows the server's own
  // row for it; with no list of the collection mounted it retires at the ack (localView.ts).
  useEffect(() => {
    if (collectionName !== 'sales' && collectionName !== 'clients') return undefined
    return localViewListMounted(collectionName)
  }, [collectionName])

  // An acknowledged pending row retires once a list has shown the server's own row for it.
  useEffect(() => {
    if (pendingRows === null || (collectionName !== 'sales' && collectionName !== 'clients')) return
    observeLocalViewRows(collectionName, idsOfRows(data.flatMap((section) => section.data)))
  }, [data, pendingRows, collectionName])

  const renderItem = ({ item, index, section }: { item: DataItem; index: number; section: DataSection }): React.ReactElement => {
    // Determine which date field to use based on whether item is from primary or secondary collection
    const itemDateField = item.isFromSecondCollection ? (secondQuery?.dateField || dateField) : dateField

    // Format the date using the appropriate date field with defensive checks
    let date: string | null = null
    try {
      const dateValue = item[itemDateField]
      if (dateValue) {
        // Get the date value, handling both Firestore Timestamp and regular Date objects
        let dateValueParsed: Date | null = null
        if (dateValue && typeof dateValue === 'object' && 'toDate' in dateValue) {
          // It's a Firestore Timestamp
          dateValueParsed = (dateValue as FirestoreTimestamp).toDate()
        } else if (dateValue instanceof Date) {
          // It's already a Date object
          dateValueParsed = dateValue
        } else if (typeof dateValue === 'number') {
          // It's a timestamp number
          dateValueParsed = new Date(dateValue)
        }

        // Only format if we have a valid date
        if (dateValueParsed && !isNaN(dateValueParsed.getTime())) {
          date = format(getLocalStoreDate(item.store, dateValueParsed), "d/M/y HH:mm")
        }
      }
    } catch (error) {
      console.error('Error formatting date:', error, item[itemDateField])
    }

    // Determine which detail screen to navigate to
    const targetScreenName = item.isFromSecondCollection
      ? (secondQuery?.detailScreenName)
      : detailScreenName

    const itemTestId = `${collectionName}-item-${item.id}`

    return (
      <Card
        style={styles.item}
        testID={itemTestId}
        {...(Platform.OS === 'web' ? { dataSet: { testid: itemTestId } } : {})}
        onPress={() => {
          if (targetScreenName) {
            // Where the row lives in the SERVER data (the detail screen edits it in place). A pending
            // row has no server position yet: its detail opens without the edit callbacks.
            const placement = serverPlacementOf(data, displayData, section, item, index)
            navigation.navigate(
              targetScreenName,
              placement
                ? {
                  ...detailScreenParams,
                  detail: item,
                  itemIndex: placement.itemIndex,
                  sectionIndex: placement.sectionIndex,
                  addItemToData,
                  editItemInData,
                  removeItemFromData,
                  preselectedStore: store
                }
                : {
                  ...detailScreenParams,
                  detail: item,
                  addItemToData,
                  preselectedStore: store
                }
            )
          }
        }}
      >
        {renderItemFieldsToShow(item, date)}
      </Card>
    )
  }

  return (
    <View style={styles.container}>
      {topView?.()}
      {/* ATL-986: never show a total computed over rows a dropped filter let through. */}
      {showTotalSum && filterError === null && <Text style={[styles.dateFilter, styles.label]} category='label'>{t('operations.totalSum', { totalSum: totalSumFormatter?.format(totalSum) })}</Text>}
      <View style={styles.dateFilter}>
        <Text category='label' style={styles.label}>
          {t('filterByDate')}
        </Text>
        <RangeDatepicker
          placement='bottom'
          min={new Date('2020-12-01')}
          max={new Date('2029-12-31')}
          dateService={localeDateService}
          range={range as DateRange}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- RangeDatepicker ref type mismatch, only blur() is used
          ref={_rangePicker as any}
          onSelect={(newRange: { startDate?: Date | null; endDate?: Date | null }) => {
            const normalizedRange: DateRange = {
              startDate: newRange.startDate ?? null,
              endDate: newRange.endDate ?? null
            }
            if (normalizedRange.endDate) {
              setRange(normalizedRange)
              void getData(normalizedRange, store, false, additionalFilters)
            } else if (normalizedRange.startDate && (range as DateRange).startDate === normalizedRange.startDate) {
              const endDate = new Date(normalizedRange.startDate)
              endDate.setHours(23, 59, 59, 99)
              setRange({ startDate: normalizedRange.startDate, endDate })
              void getData({ startDate: normalizedRange.startDate, endDate }, store, false, additionalFilters)
            } else {
              setRange(normalizedRange)
            }
          }}
        />
      </View>
      {showStoreFilter && <StoreChooserHorizontal store={store} style={{}} onChange={(newStore: string) => {
        setStore(newStore)
        void getData(range, newStore, false, additionalFilters)
      }} />}


      {
        filterError !== null
          ? (
            <Text status='danger' style={styles.filterErrorText} testID='operations-filter-error'>
              {t('operations.filterUnavailable')}
            </Text>
          )
          : isLoading
          ? (
            <Loader />
          )
          : (
            <SectionList
              contentContainerStyle={styles.list}
              sections={displayData}
              keyExtractor={(item) => item.id}
              renderItem={renderItem}
              ListEmptyComponent={() => (<Text appearance='hint' style={styles.noItemsText}>
                {t('noItemsText')}
              </Text>
              )}
              renderSectionHeader={({ section: { title } }) => (
                <Text category='label' style={styles.label}>{title}</Text>
              )}
              // eslint-disable-next-line react-native/no-inline-styles
              renderSectionFooter={() => <View style={{ height: 50 }}></View>}
              ListFooterComponent={() => {
                if (isBottomLoading) {
                  return <Loader />
                }
                if (showExportButtons && exportDataTransformer && data.length > 0 && canExport) {
                  return (
                    <View style={styles.exportButtonsContainer}>
                      <Button appearance="ghost" status='basic' onPress={() => handleExport('csv')}>{t('csvExport')}</Button>
                      <Button appearance="ghost" status='basic' onPress={() => handleExport('excel')}>{t('excelExport')}</Button>
                    </View>
                  )
                }
                return null
              }}
              ListHeaderComponent={Platform.OS === 'web' && getDataMode === 'lazyLoading' ? <WebRefreshControl refreshFunction={() => {
                void getData(range, store, true, additionalFilters)
                setListRefreshing(true)
              }} listRefreshing={listRefreshing} refreshText={t('refresh')} primaryColor={colors.primary} /> : undefined}
              onEndReached={() => {
                if (getDataMode === 'lazyLoading' && !isBottomLoading && lastDocument !== 'end') {
                  void getData(range, store, false, additionalFilters)
                }
              }}
              refreshing={listRefreshing}
              onRefresh={() => {
                if (getDataMode === 'lazyLoading') {
                  void getData(range, store, true, additionalFilters)
                  setListRefreshing(true)
                }
              }}
            />
          )
      }

    </View>
  )
})

export default OperationsList

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  dateFilter: {
    marginHorizontal: 20,
    marginBottom: 10
  },
  list: {
    paddingHorizontal: 20,
  },
  noItemsText: {
    marginTop: 100,
    textAlign: 'center'
  },
  filterErrorText: {
    marginTop: 60,
    marginHorizontal: 30,
    textAlign: 'center'
  },
  label: {
    marginTop: 10,
    marginBottom: 5
  },
  item: {
    marginVertical: 4
  },
  exportButtonsContainer: {
    marginVertical: 20,
    alignItems: 'center',
  }
})
